import { devLog, alwaysLog } from '@/utils/logger';
import { file, write } from 'opfs-tools';
import { opfsErrorHandler, OPFSErrorHandler } from './opfsErrorHandler';

/**
 * OPFS IO 工具类
 * 提供统一的文件操作接口，包含错误处理和重试机制
 */
export class OpfsIO {
  /**
   * 写入文件（如果不存在）
   * @param path OPFS文件路径
   * @param stream 文件流
   * @param options 写入选项
   */
  static async writeIfAbsent(
    path: string, 
    stream: ReadableStream<Uint8Array>,
    options: { maxRetries?: number; retryDelay?: number } = {}
  ): Promise<void> {
    return opfsErrorHandler.withRetry(
      async() => {
        const opfsFile = file(path);
        if (!(await opfsFile.exists())) {
          await write(path, stream);
        }
      },
      `写入文件 ${path}`,
      { maxRetries: 2, retryDelay: 150, ...options }
    );
  }

  /**
   * 读取文件为Blob
   * @param path OPFS文件路径
   * @param mimeType MIME类型
   * @param createBlobUrl 是否创建blob URL
   * @param options 读取选项
   */
  static async readAsBlob(
    path: string,
    mimeType: string,
    createBlobUrl = true,
    options: { maxRetries?: number; retryDelay?: number } = {}
  ): Promise<{ blob: Blob; blobUrl?: string }> {
    return opfsErrorHandler.withRetry(
      async() => {
        const opfsFile = file(path);
        if (!(await opfsFile.exists())) {
          throw new Error(`OPFS文件不存在: ${path}`);
        }

        // 从OPFS读取文件
        const stream = await opfsFile.stream();
        const chunks: Uint8Array[] = [];
        const reader = stream.getReader();
        
        // 使用递归函数避免循环中的await
        const readChunks = async() => {
          const { done, value } = await reader.read();
          if (done) {
            return;
          }
          chunks.push(value);
          await readChunks();
        };
        await readChunks();

        // 合并chunks创建blob
        const blob = new Blob(chunks, { type: mimeType });
        
        let blobUrl: string | undefined;
        if (createBlobUrl) {
          blobUrl = URL.createObjectURL(blob);
        }

        return { blob, blobUrl };
      },
      `读取文件 ${path}`,
      { maxRetries: 3, retryDelay: 200, ...options }
    );
  }

  /**
   * 读取文件流
   * @param path OPFS文件路径
   * @param options 读取选项
   */
  static async readStream(
    path: string,
    options: { maxRetries?: number; retryDelay?: number } = {}
  ): Promise<ReadableStream<Uint8Array>> {
    return opfsErrorHandler.withRetry(
      async() => {
        const opfsFile = file(path);
        if (!(await opfsFile.exists())) {
          throw new Error(`OPFS文件不存在: ${path}`);
        }
        return opfsFile.stream();
      },
      `读取文件流 ${path}`,
      { maxRetries: 3, retryDelay: 200, ...options }
    );
  }

  /**
   * 检查文件是否存在
   * @param path OPFS文件路径
   */
  static async exists(path: string): Promise<boolean> {
    try {
      const opfsFile = file(path);
      return await opfsFile.exists();
    } catch (error) {
      devLog.warn(`[OpfsIO] 检查文件存在性失败: ${path}`, error);
      return false;
    }
  }

  /**
   * 删除文件（如果存在）
   * @param path OPFS文件路径
   * @param options 删除选项
   */
  static async removeIfExists(
    path: string,
    options: { maxRetries?: number; retryDelay?: number } = {}
  ): Promise<void> {
    return opfsErrorHandler.withRetry(
      async() => {
        const opfsFile = file(path);
        if (await opfsFile.exists()) {
          await opfsFile.remove();
        }
      },
      `删除文件 ${path}`,
      { maxRetries: 2, retryDelay: 100, ...options }
    );
  }

  /**
   * 写入Blob到OPFS
   * @param path OPFS文件路径
   * @param blob 文件blob
   * @param options 写入选项
   */
  static async writeBlob(
    path: string,
    blob: Blob,
    options: { maxRetries?: number; retryDelay?: number } = {}
  ): Promise<void> {
    return OpfsIO.writeIfAbsent(path, blob.stream(), options);
  }

  /**
   * 检查是否为OPFS相关错误
   * @param error 错误对象
   */
  static isOpfsError(error: any): boolean {
    return OPFSErrorHandler.isOPFSError(error);
  }

  /**
   * 获取OPFS错误信息
   * @param error 错误对象
   */
  static getOpfsErrorInfo(error: Error): { type: string; message: string } {
    return OPFSErrorHandler.getOPFSErrorInfo(error);
  }

  /**
   * 清理目录下过期的文件
   * @param dirPath 目录路径
   * @param maxAgeMs 最大生存时间（毫秒）
   * @returns 删除的文件数量
   */
  static async cleanExpiredFiles(dirPath: string, maxAgeMs: number): Promise<number> {
    try {
      const root = await navigator.storage.getDirectory();
      let currentDir = root;
      
      // 遍历到目标目录
      const parts = dirPath.split('/').filter(p => p);
      for (const part of parts) {
        try {
          // eslint-disable-next-line no-await-in-loop
          currentDir = await currentDir.getDirectoryHandle(part, { create: false });
        } catch {
          // 目录不存在
          return 0;
        }
      }
      
      let deletedCount = 0;
      const now = Date.now();

      // @ts-ignore - TS可能还没完全支持AsyncIterableIterator
      for await (const [name, handle] of currentDir.entries()) {
        /* eslint-disable-next-line no-continue -- 仅跳过目录项，比嵌套 if 更清晰 */
        if (handle.kind !== 'file') continue;

        try {
          const fileHandle = handle as FileSystemFileHandle;
          const fileObj = await fileHandle.getFile();
          if (now - fileObj.lastModified > maxAgeMs) {
            await currentDir.removeEntry(name);
            devLog.log(`[OpfsIO] 删除过期文件: ${dirPath}/${name}, age: ${(now - fileObj.lastModified) / 1000}s`);
            deletedCount++;
          }
        } catch (e) {
          devLog.warn(`[OpfsIO] 检查/删除文件失败: ${name}`, e);
        }
      }
      return deletedCount;
    } catch (error) {
      devLog.warn(`[OpfsIO] 清理目录失败: ${dirPath}`, error);
      return 0;
    }
  }

  /**
   * 删除目录下所有文件
   * @param dirPath 目录路径
   */
  static async removeAllFiles(dirPath: string): Promise<number> {
    try {
      const root = await navigator.storage.getDirectory();
      let currentDir = root;
      
      const parts = dirPath.split('/').filter(p => p);
      for (const part of parts) {
        try {
          // eslint-disable-next-line no-await-in-loop
          currentDir = await currentDir.getDirectoryHandle(part, { create: false });
        } catch {
          return 0;
        }
      }
      
      let deletedCount = 0;
      // @ts-ignore
      for await (const [name, handle] of currentDir.entries()) {
        if (handle.kind === 'file') {
          await currentDir.removeEntry(name);
          deletedCount++;
        }
      }
      return deletedCount;
    } catch (error) {
      devLog.warn(`[OpfsIO] 清空目录失败: ${dirPath}`, error);
      return 0;
    }
  }
}

// 导出便捷函数
export const writeIfAbsent = OpfsIO.writeIfAbsent;
export const readAsBlob = OpfsIO.readAsBlob;
export const readStream = OpfsIO.readStream;
export const exists = OpfsIO.exists;
export const removeIfExists = OpfsIO.removeIfExists;
export const writeBlob = OpfsIO.writeBlob;
export const isOpfsError = OpfsIO.isOpfsError;
export const getOpfsErrorInfo = OpfsIO.getOpfsErrorInfo;
export const cleanExpiredFiles = OpfsIO.cleanExpiredFiles;
export const removeAllFiles = OpfsIO.removeAllFiles;
